import React, {
	useEffect,
	useState,
	Fragment,
	useContext,
	useCallback,
} from "react";
import { Context } from "Context";
import Content from "components/layout/Content";
import MoreHelp from "components/aside/MoreHelp";
import Breadcrumbs from "components/layout/Breadcrumbs";
import useCheckMediaQuery from "hooks/useCheckMediaQuery";
import useFetch from "hooks/useFetch";
import {
	mapString,
	addClassToWrapper,
	triggerInterstitialByClassName,
	isCurrentPathPresent,
	addClassToBody,
	resolveParamToSession,
} from "utils/common";
import { getCookie } from "utils/cookie";
import { loadCdnCssFile } from "utils/env";
import Search from "components/search/Search";
import Error from "components/shared/Error";
import { useParams, useLocation } from "react-router-dom";
import Heading, { HEADING_TYPES } from "components/shared/Heading";
import { trackPage } from "utils/analytics";
import { validateIfThemeShouldBeLoaded } from "utils/theme";

const cacheMinutes = 5;

const replaceElementContent = (searchString, newContent) => {
	const element = document.querySelector(searchString);
	if (element) {
		element.content = newContent;
	}
};

const Layout = () => {
	const groups = {
		help: "help",
		faq: "faq",
	};
	const {
		config,
		profileData: { userType },
		setGroup,
		interstitials,
		setInterstitials,
		analyticsConfig,
		config: { brand },
		setModal,
	} = useContext(Context);

	const { pathname } = useLocation();
	const modernContent = resolveParamToSession();
	const geoAkaCookie = getCookie("geolocation_aka_jar", true);
	const geoCookie = getCookie("GEOLOCATION_jar", true);
	const geo = geoAkaCookie || geoCookie;
	const swid = getCookie("SWID");
	const siteKey = pathname
		.split("/")
		.filter((e) => e)
		.join(".");
	const analyticsMetaData = {
		site: brand,
		guestType: userType,
		customerId: swid,
		registrationStatus: swid ? "Registered - Logged In" : "Not Registered",
		geo: {
			dma: geo.metro,
			dmaCode: geo.metrocode,
			country: geo.country,
			region: geo.region,
		},
		pageKey: `${brand}.site.${siteKey}`,
		storeType: "Consumer",
	};

	const { group, category, subcategory } = useParams();
	const [module, setModule] = useState(null);
	const [contentEndpoint, setContentEndpoint] = useState(null);
	const [pageError, setPageError] = useState(false);
	const mainClass = "help-layout";
	const isMobileView = useCheckMediaQuery(config.mobileMediaQuery);
	const { response, loading, error } = useFetch(contentEndpoint, {
		options: {},
		trigger: contentEndpoint,
		cacheValidMinutes: cacheMinutes,
	});

	const buildAnalyticsSiteSection = () => {
		let pathElements = pathname.split("/");
		pathElements = pathElements.filter((element) => element);
		const pageId = pathElements.pop();
		const siteSection = `${analyticsConfig.baseSiteSection}/${pathElements.join(
			"/"
		)}`;
		return { pageId, siteSection };
	};

	const setPageMetadata = useCallback(() => {
		document.title = module?.meta?.pageTitle || "";

		if (module?.meta?.pageTitle) {
			replaceElementContent("meta[property='og:title']", module.meta.pageTitle);
		}

		replaceElementContent(
			"meta[property='og:url']",
			`${window.location.origin}${window.location.pathname}`
		);

		if (module?.meta?.description) {
			let descriptionElement = document.querySelector(
				"meta[name='description']"
			);
			if (descriptionElement) {
				descriptionElement.content = module.meta.description;
			} else {
				descriptionElement = document.createElement("meta");
				descriptionElement.name = "description";
				descriptionElement.content = module.meta.description;
				document.head.appendChild(descriptionElement);
			}

			replaceElementContent(
				"meta[property='og:description']",
				module.meta.description
			);
		}

		if (module?.meta?.keywords) {
			let keywordsElement = document.querySelector("meta[name='keywords']");
			if (keywordsElement) {
				keywordsElement.content = module.meta.keywords;
			} else {
				keywordsElement = document.createElement("meta");
				keywordsElement.name = "keywords";
				keywordsElement.content = module.meta.keywords;
				document.head.appendChild(keywordsElement);
			}
		}
		const themesToLoad = Object.keys(module?.theme || {});
		if (themesToLoad.length) {
			themesToLoad.forEach((theme) => {
				if (
					isCurrentPathPresent(module.theme[theme].allowedPagesToApplyThemes)
				) {
					const themePath = mapString(config.themePath, theme);
					loadCdnCssFile(config.helpAssetsUrl, themePath);
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [module]);

	useEffect(() => {
		window.scrollTo(0, 0);
		const themes = {};
		Object.assign(themes, config.themes, module?.theme);
		validateIfThemeShouldBeLoaded(themes, brand);
		addClassToWrapper(config.pathsToAddModifier);
		addClassToBody(
			config.brands[brand]?.pathToAddClass?.paths,
			config.brands[brand]?.pathToAddClass?.class
		);
	}, [pathname, config, brand, module]);

	useEffect(() => {
		setGroup(group);
		if (userType && config.defaultBaseApiUrl && config.content?.path) {
			const apiUrl = mapString(
				`${config.defaultBaseApiUrl}${config.content.path}`,
				group,
				category,
				subcategory,
				modernContent
			).split("/undefined")[0];
			setContentEndpoint(
				`${apiUrl}?userType=${userType}${
					config.store ? `&store=${config.store}` : ""
				}${modernContent ? `&modernContent=${modernContent}` : ""}`
			);
		}
	}, [
		userType,
		config.defaultBaseApiUrl,
		config.content?.path,
		config.store,
		group,
		category,
		subcategory,
		setGroup,
		modernContent,
	]);

	useEffect(() => {
		if (response) {
			setModule(response);
			response.interstitials && setInterstitials(response.interstitials);
			setPageError(false);
			const { pageId, siteSection } = buildAnalyticsSiteSection();
			trackPage(pageId, {
				siteSection,
				...analyticsMetaData,
				...analyticsConfig,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [response]);

	useEffect(() => {
		triggerInterstitialByClassName(interstitials, setModal);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [interstitials]);

	useEffect(() => {
		if (error) {
			setModule(error.body);
			setPageError(true);
			trackPage("404", {
				siteSection: "content/error",
				...analyticsMetaData,
				...analyticsConfig,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	useEffect(() => {
		if (module) {
			setPageMetadata();
		}
	}, [module, setPageMetadata]);

	return (
		<Fragment>
			<div className={`${mainClass} ${mainClass}--${group}`}>
				{!isMobileView && <Breadcrumbs module={module?.breadcrumbs} loading={loading} />}
				{
					loading ? <div className={`${mainClass}__title--loading`} /> : module?.pageHeader?.sections?.title ?
					<Heading
						className={`${mainClass}__title`}
							content={module?.pageHeader?.sections?.title}
							ariaLabel={module?.pageHeader?.sections?.title}
						/> : null
				}
				{
					loading ? <div className={`${mainClass}__search--loading`} /> :
						module?.search && !pageError ? (
					<section className={`${mainClass}__search`}>
						<Heading
							type={HEADING_TYPES.h2}
							className={`${mainClass}__search__title`}
							content={module.search?.title}
						/>
							<Search
								group={group}
								decorativeImage={module?.decorativeImage}
								search={module.search}
								swid={swid}
							/>
					</section>
						) : null}
				<main className={`${mainClass}__container`}>
					{group === groups.faq && !loading && module?.slug && (
						<Fragment>
							<Heading
								type={HEADING_TYPES.h2}
								className={`${mainClass}__header--faq`}
								content={module.slug}
							/>
						</Fragment>
					)}
					<section className={`${mainClass}__content`}>
						{pageError && module?.error && <Error {...module.error} />}
						{module && !loading && <Content module={module} />}
					</section>
					{module?.moreHelp && (
						<aside
							className={`${mainClass}__aside${
								module.moreHelp.callToActions?.length
									? ""
									: ` ${mainClass}__content--empty-actions`
							}`}
						>
							{module?.moreHelp.map((moreHelp, i) => (
								<MoreHelp
									key={i}
									isMobileView={isMobileView}
									brand={brand}
									title={moreHelp.title}
									callToActions={moreHelp.callToActions}
									primary={moreHelp.primary}
									secondary={moreHelp.secondary}
									items={moreHelp.items}
								/>
							))}
						</aside>
					)}
				</main>
			</div>
		</Fragment>
	);
};

export default Layout;
